'use client';

import clsx from 'clsx';
import { ImageCouldFromRemoteServer as Image } from '@/components/images'
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { Button } from '@nf/ui';
import { headerStyle } from '../header.style';
import { DropdownIcon, QuestionIcon } from '@/components/icons/svg-icons';
import { NewFeatureBadge } from '@/components/new-tag/new-feature-badge';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from '@/navigation';
import { useSiteStore } from '@/store/site';
import { ProductItem, SubItem, useProductList } from '@/hooks';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useOuterClick } from '@nf/utils-common/hooks';
import { menuDefinition } from '@/types/menu-setting';
import { tooltip } from 'styled-system/recipes';
import { ViewEnum } from '@/types';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { useAppearanceStore } from '@/store/appearance';
import { useAuthToken } from '@/store/auth';
import { modalService } from '@/services/modal.service';
import { TutorialTooltip } from '@/features/tutorial/tutorial-tooltip';
import { isNumberGames } from '@/services/settings.service';
import { sportService } from '@/services/sport.service';
import { useCurrentProperties } from '@/hooks/query';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { setKafkaNormal, setKafkaStreaming } from '@/hooks/query/use-kafka-log';
import { KafkaNote, StreamingKafkaNote } from '@nf/utils-common/constants';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { getKafkaStreamerType } from '@/utils/common-util';
import { sportsbookService } from '@/services/sportsbook.service';
import { matchEquals, MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { useAccountInfo } from '@/hooks/query/account';
import { sportPickTagMap } from '@/settings/sports/sport-new-tag-map';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';

export const HeaderProduct = () => {
	const selectedLayoutSegments = useSelectedLayoutSegments();
	const { sport } = useCurrentProperties();
	const [isProductListOpen, setIsProductListOpen] = useState(false);
	const [openedLabelKeySubMenu, setOpenedLabelKeySubMenu] = useState<ProductItem['labelKey']>('');
	const { SiteConfigsData } = useGetSiteConfigs();
	const [isParlayMode, selectedProductPage] = useSiteStore(state => [state.isParlayMode, state.currentPage]);
	const productList = useProductList();
	const productListRef = useRef<HTMLDivElement>(null);
	useOuterClick(productListRef, () => setIsProductListOpen(false));
	const t = useTranslations('General');
	const locale = useLocale();
	const [view, oddsPageLayout] = useAppearanceStore(state => [state.view, state.oddsPageLayout]);
	const isShowTutorialButton = selectedLayoutSegments.includes('(main)') && view === ViewEnum.mars && oddsPageLayout === 'list';
	const isPromotionPage = selectedLayoutSegments.includes('promotion');
	const isStreamerPage = selectedLayoutSegments.includes('streamer');
	const router = useRouter();
	const analyseStreamersRef = useAnalyseAttribute('open-streamers-list-from-header-button');

	let currentPage = 'lbl_Sports';
	if (isPromotionPage) {
		currentPage = 'lbl_ECPPromotion';
	} else if (isStreamerPage) {
		currentPage = 'lbl_Streamer';
	} else if (sportService.isAnyVirtualSportsProduct(sport)) {
		currentPage = selectedProductPage === 'lbl_Sports' ? 'lbl_Sports' : 'lbl_VirtualSport';
	} else if (isParlayMode) {
		currentPage = 'lbl_mixparlay'
	} else if (selectedLayoutSegments.includes('world')) {
		const index = selectedLayoutSegments.findIndex(x => x === 'world');
		const cupName = selectedLayoutSegments[index + 1];
		const menuMode = Object.keys(menuDefinition).find(key => menuDefinition[key as keyof typeof menuDefinition] == cupName)?.substring(1);
		currentPage = sportsbookService.makeMenuNodeResourceKey(SiteConfigsData?.EnableFilterSaba, menuMode);
	} else if (isNumberGames(sport)) {
		currentPage = 'lbl_161';
	} else {
		productList.forEach(product => {
			if (product.path) {
				if (selectedLayoutSegments.includes(product.path.substring(1))) {
					currentPage = product.labelKey;
				}
			}
		});
	}

	const toggleProductList = () => {
		setKafka('product');
		setIsProductListOpen(!isProductListOpen);
	}

	const openBettingTutorial = () => {
		setKafka('tutorial');
		modalService.openNewbieTutorialModal();
	}

	const setKafka = (from: 'product' | 'tutorial') => {
		const kafkaData = {
			note: KafkaNote.MarsV1,
			type: 2,
			action: from === 'product' ? 1 : 18
		}
		setKafkaNormal(kafkaData);
	}

	return (
		<div className={headerStyle.products}>
			<Button
				className={clsx(
					headerStyle.productButton,
					{ [headerStyle.productButtonOpen]: isProductListOpen }
				)}
				variant="classic"
				onClick={toggleProductList}
			>
				<span className={headerStyle.productButtonText}>{t(currentPage)}</span>
				<DropdownIcon />
			</Button>
			<div
				ref={productListRef}
				className={clsx(
					'product-list',
					headerStyle.productList,
					{ [headerStyle.productListOpen]: isProductListOpen }
				)}
			>
				<div className={headerStyle.productListContainer}>
					{
						isProductListOpen ?
							productList.map(item => (
								<Product
									key={item.labelKey}
									{...item}
									currentPage={currentPage}
									closeProductList={() => setIsProductListOpen(false)}
									openedLabelKeySubMenu={openedLabelKeySubMenu}
									setOpenedLabelKeySubMenu={setOpenedLabelKeySubMenu}
								/>
							)) : null
					}
				</div>
			</div>
			{isShowTutorialButton ? (
				<Button
					className={headerStyle.newbieInfoButton}
					variant="icon"
					onClick={openBettingTutorial}
				>
					<QuestionIcon />
					<TutorialTooltip className={tooltip({ visual: 'basic', size: 'sm' })} />
				</Button>
			) : null}
			{SiteConfigsData?.EnableStreamerSchedule && !isStreamerPage ?
				<Button
					ref={analyseStreamersRef}
					className={headerStyle.streamerButton}
					variant="icon"
					onClick={() => {
						wcPromotionMissionService.notifyMission({
							taskType: TaskTypeEnum.ClickSabaFeatures,
							productCode: 'streamer'
						});

						router.push('/streamer', { locale });
						if (view === ViewEnum.mars) {
							const streamerKafkaData = {
								note: StreamingKafkaNote.OddsPage,
								type: 16,
								action: 1,
								matchId: 0,
								streamer_type: getKafkaStreamerType(0)
							};
							const kafkaData = {
								note: KafkaNote.MarsV1,
								type: 5,
								action: 1
							}
							setKafkaStreaming(streamerKafkaData);
							setKafkaNormal(kafkaData);
						}
					}}
				>
					<Image
						src="/images/live-streamer/streamer-icon.svg"
						alt=""
						width="20"
						height="20"
						isGalaxyFolder={true}
					/>
				</Button> : null}
		</div>
	);
};

type ProductProps = ProductItem & {
	currentPage: string;
	openedLabelKeySubMenu: string;
	setOpenedLabelKeySubMenu: Dispatch<SetStateAction<string>>;
	closeProductList: () => void;
};

const Product = ({
	labelKey,
	subMenu,
	openedLabelKeySubMenu,
	newUntil,
	setOpenedLabelKeySubMenu,
	path,
	productName,
	onClick,
	currentPage,
	closeProductList
}: ProductProps) => {
	const { filterSabaString } = useFilterSabaString();
	const [
		resetAllFilter,
	] = useSiteStore(state => [
		state.resetAllFilter,
	]);
	const setCurrentPage = useSiteStore(state => state.setCurrentPage);
	const { disableParlay } = useOddsPageSwitcher();
	const router = useRouter();
	const locale = useLocale();
	const t = useTranslations('General');
	const { user } = useAccountInfo();
	const authToken = useAuthToken();
	const isBefore = !authToken;

	const nextAnalyseKey = !path 
		? '' 
		: path === '/favorites' 
			? 'switch-to-my-favorites-via-product-list' 
			: path === '/promotion' 
				? 'switch-to-promotion-via-product-list' 
				: 'switch-to-odds-page-via-product-list';
	const newNextAnalyseKey = matchEquals(labelKey)
		.on('lbl_VirtualSport', () => 'switch-to-virtual-sports-via-product-list')
		.on('lbl_161', () => 'switch-to-number-game-via-product-list')
		.otherwise(() => nextAnalyseKey);
	const taggedElement = useAnalyseAttribute(newNextAnalyseKey);

	const productPickTagConfig = path === '/promotion' && (user?.EnableSBRWidget || isBefore) ? sportPickTagMap.sbr : undefined;

	const onClickProduct = () => {
		if (labelKey === 'lbl_Sports' || labelKey === 'lbl_mixparlay' || labelKey === 'lbl_VirtualSport') {
			setCurrentPage(labelKey);
		}
		
		if (labelKey == 'lbl_ESports') {
			setEsportsKafka();
		} else {
			setKafka();
		}

		// There’s no need to distinguish the [taskType] here.
		wcPromotionMissionService.notifyMission({
			taskType: TaskTypeEnum.ClickVirtualSports,
			productCode: productName
		});

		if (!subMenu) {
			closeProductList();
			disableParlay();

			if (path) {
				resetAllFilter();
				router.push(path, { locale });
			}
			if (onClick) {
				onClick();
			}
		} else {
			MaybeMatch(openedLabelKeySubMenu === labelKey)
				.fold(
					() => setOpenedLabelKeySubMenu(labelKey), 
					() => setOpenedLabelKeySubMenu('')
				)
		}
	}

	const setEsportsKafka = () => {
		const kafkaData = {
			note: KafkaNote.EsportsWeb,
			type: 15,
			action: 3
		}
		setKafkaNormal(kafkaData);
	}

	const setKafka = () => {
		if (labelKey === 'lbl_PredictMarket') {
			setKafkaNormal({ note: KafkaNote.PredictionMarket, type: 1, action: 7 });
			return;
		}
		if (window.innerWidth < 750) {
			if (labelKey === 'Menu2') {
				setKafkaNormal({ note: KafkaNote.MarsV1, type: 2, action: 21 });
				return;
			}
			if (labelKey.includes('Menu50')) {
				setKafkaNormal({ note: KafkaNote.MarsV1, type: 2, action: 22 });
				return;
			}
			if (labelKey.includes('Menu51')) {
				setKafkaNormal({ note: KafkaNote.MarsV1, type: 2, action: 23 });
				return;
			}
			if (labelKey.includes('Menu52')) {
				setKafkaNormal({ note: KafkaNote.MarsV1, type: 2, action: 24 });
				return;
			}
		}

		let action;
		switch (labelKey) {
			case 'lbl_Streamer': // streamer schedule
				action = 9;
				break;
			case 'lbl_ECPPromotion': // promotion
				action = 17;
				break;
			case 'Menu3': // euro cup
				action = 9;
				break;
			case 'lbl_Sports': // sports
				action = 8;
				break;
			case 'lbl_mixparlay': // parlay
				action = 11;
				break;
			case 'lbl_favorite': // my fav
				action = 10;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: (labelKey === 'lbl_Streamer') ? 5 : 2,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	return (
		<div
			className={clsx('product-list__item', headerStyle.productItem)}
			ref={taggedElement}
		>
			{newUntil ? (
				<NewFeatureBadge timeRange={newUntil} />
			) : null}
			
			<Button
				variant="classic"
				className={headerStyle.productItemButton}
				data-selected={(currentPage === labelKey) || (openedLabelKeySubMenu === labelKey)}
				data-has-submenu={subMenu && subMenu.length > 0}
				onClick={() => onClickProduct()}
			>
				{filterSabaString(t(labelKey))}
				{productPickTagConfig ? (
					<NewFeatureBadge 
						variant="pick"
						text={t('lbl_SBRPicksIcon')}
						timeRange={productPickTagConfig}
					/>
				) : null}
			</Button>
			{subMenu ? (
				<div className={clsx('submenu', headerStyle.subMenu)}>
					{subMenu.map(subItem => (
						<SubProduct
							key={subItem.labelKey}
							{...subItem}
							closeProductList={closeProductList}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

type SubProductProps = SubItem & {
	closeProductList: () => void;
};

const SubProduct = ({
	productName,
	labelKey,
	imagePath,
	onClick,
	closeProductList
}: SubProductProps) => {
	const t = useTranslations('General');
	const { filterSabaString } = useFilterSabaString();

	const clickProduct = () => {
		wcPromotionMissionService.notifyMission({
			taskType: TaskTypeEnum.ClickVirtualSports,
			productCode: productName
		});

		if (onClick) {
			onClick();
		}
		closeProductList();
	}

	return (
		<div
			className={clsx('submenu__item', headerStyle.submenuItem)}
			onClick={clickProduct}
		>
			<div className={headerStyle.submenuImg}>
				<Image
					width={112}
					height={58}
					src={`/images/home/product-game/${imagePath}.png`}
					alt=""
					isGalaxyFolder={true}
				/>
			</div>
			<div className={headerStyle.submenuText}>{filterSabaString(t(labelKey))}</div>
		</div>
	);
}
